import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslatePipe } from "@ngx-translate/core";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DsoCacheService } from "../dso-edit/dso-cache.service";
import { DsoWebinstallningarModel, SearchQuery } from "../models/dso-models";
import { DsoSearchService } from "../services/dso-search.service";
import { DsoService } from "../services/dso.service";

@Component({
    selector: "rask-dso-search",
    templateUrl: "./dso-search.component.html",
    styleUrls: ["./dso-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent,
        MultiSelectorComponent,
        InputComponent,
        RadiobuttonGroupComponent,
        SubmitButtonComponent,
        TranslatePipe
    ]
})
export class DsoSearchComponent implements OnInit {
    aktivRadiobuttons: IRadiobuttonItem[] = [
        { key: true,
            value: "Ja" },
        { key: false,
            value: "Nej" }
    ];
    form = new UntypedFormGroup({
        handelssortiment: new UntypedFormControl([]),
        mottagningsplatser: new UntypedFormControl([]),
        fritext: new UntypedFormControl(),
        aktiv: new UntypedFormControl(true)
    });
    handelssortimentsgrupper: SelectionItem[];

    private readonly WIK = "Destineringsomraden.search";
    private readonly cacheService = inject(DsoCacheService);
    private readonly dsoSearchService = inject(DsoSearchService);
    private readonly dsoService = inject(DsoService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    initData() {
        this.cacheService.initiated.subscribe(isit => {
            if (isit) {
                this.webinstallningarService.getAsync(this.WIK).then((res: DsoWebinstallningarModel) => {
                    this.handelssortimentsgrupper = this.dsoSearchService.setFilter(this.cacheService.basdata.handelssortimentgrupper, res);
                    if (ObjectUtils.hasValue(res)) {
                        this.form.get("mottagningsplatser").setValue(res.mottagningsplatser);
                        this.form.get("fritext").setValue(res.fritext);
                        this.form.get("aktiv").setValue(res.aktiv ?? true);
                    }
                });
            }
        });
        this.cacheService.search({
            fritext: "",
            aktiv: true,
            handelssortimentIdList: [],
            mottagningsplatsIdList: []
        }, true);
    }

    ngOnInit(): void {
        this.initData();
    }

    search() {
        const query = {
            fritext: this.form.get("fritext").value,
            aktiv: this.form.get("aktiv").value,
            handelssortimentIdList: this.dsoSearchService.getSelectedHandelssortiment(this.handelssortimentsgrupper).map(hs => hs.id),
            mottagningsplatsIdList: this.form.get("mottagningsplatser").value.map(x => x.id)
        } as SearchQuery;
        this.cacheService.search(query);
        this.saveWebinstallnigar();
    }

    searchForMottagningsplatsTypeAhead = (searchString: string) => this.dsoService.searchForMottagningsplatsTypeAhead(searchString, null);

    private saveWebinstallnigar() {
        const obj = {
            handelssortiment: this.dsoSearchService.getSelectedHandelssortiment(this.handelssortimentsgrupper),
            mottagningsplatser: this.form.get("mottagningsplatser").value,
            fritext: this.form.get("fritext").value,
            aktiv: this.form.get("aktiv").value
        } as DsoWebinstallningarModel;

        this.webinstallningarService.set(this.WIK, obj);
    }
}
